/**
 * Checkbox Component
 *
 * Custom styled checkbox with label support.
 */

import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { CheckIcon, MinusIcon } from '@heroicons/react/24/solid';

import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> {
  /** Label text or element displayed next to checkbox */
  label?: ReactNode;
  /** Description text below the label */
  description?: string;
  /** Alias for description */
  helperText?: string;
  /** Alias for description */
  hint?: string;
  /** Whether the checkbox has an error state */
  error?: boolean;
  /** Whether the checkbox is in indeterminate state (for "select all" checkboxes) */
  indeterminate?: boolean;
  /** Callback when checked state changes (alternative to onChange) */
  onCheckedChange?: (checked: boolean) => void;
  /** Value can be boolean for react-hook-form compatibility */
  value?: string | number | readonly string[] | boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, helperText, hint, error, indeterminate, id, onCheckedChange, onChange, value, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const displayDescription = description ?? helperText ?? hint;
    const internalRef = useRef<HTMLInputElement>(null);

    // Handle indeterminate state (not available via HTML attribute)
    useEffect(() => {
      const element = internalRef.current;
      if (element) {
        element.indeterminate = indeterminate ?? false;
      }
    }, [indeterminate]);

    // Merge refs
    const setRefs = (element: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    };

    // Filter out boolean value (react-hook-form compatibility) - checkbox uses checked, not value
    const inputValue = typeof value === 'boolean' ? undefined : value;

    const isIndeterminate = indeterminate && !props.checked;

    return (
      <div className={cn('flex items-start gap-3', className)}>
        {/* GAP-006: Touch target wrapper - 44px minimum on mobile */}
        <div className="relative flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center md:min-h-[24px] md:min-w-[24px]">
          <input
            type="checkbox"
            ref={setRefs}
            id={checkboxId}
            onChange={handleChange}
            value={inputValue}
            className={cn(
              'peer h-5 w-5 cursor-pointer appearance-none rounded border bg-white transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20 focus:ring-offset-2',
              'checked:border-mv-blue-600 checked:bg-mv-blue-600',
              'disabled:cursor-not-allowed disabled:opacity-50',
              // GAP-006: Extend touch target beyond visual checkbox
              'before:absolute before:inset-0 before:content-[""]',
              error ? 'border-mv-red-600' : 'border-mv-gray-300',
              // Indeterminate state styling
              isIndeterminate && 'border-mv-blue-600 bg-mv-blue-600'
            )}
            {...props}
          />
          {/* Show minus icon for indeterminate, check icon for checked */}
          {isIndeterminate ? (
            <MinusIcon
              className="pointer-events-none absolute h-3.5 w-3.5 text-white"
              aria-hidden="true"
            />
          ) : (
            <CheckIcon
              className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
              aria-hidden="true"
            />
          )}
        </div>
        {(label || displayDescription) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={checkboxId}
                className="cursor-pointer text-sm font-medium text-mv-gray-700"
              >
                {label}
              </label>
            )}
            {displayDescription && (
              <span className="text-sm text-mv-gray-500">{displayDescription}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
